import api from './api';
import { handleResponse, handleError } from './api';

/**
 * Gatherings — collections of events a Club hosts (day-grouped agenda + RSVP).
 */
const gatheringService = {
  async listForClub(clubId) {
    try { return handleResponse(await api.get(`/clubs/${clubId}/gatherings`)); }
    catch (e) { return handleError(e); }
  },

  async create(clubId, { name, description, startsAt, endsAt, isPrivate = true, mode, fundingType, entryFeeCents, fundingGoalCents, fundingDeadline } = {}) {
    try { return handleResponse(await api.post(`/clubs/${clubId}/gatherings`, { name, description, startsAt, endsAt, isPrivate, mode, fundingType, entryFeeCents, fundingGoalCents, fundingDeadline })); }
    catch (e) { return handleError(e); }
  },

  // Standalone gathering (no club).
  async createStandalone({ name, description, startsAt, endsAt, isPrivate = true, mode, fundingType, entryFeeCents, fundingGoalCents, fundingDeadline } = {}) {
    try { return handleResponse(await api.post('/gatherings', { name, description, startsAt, endsAt, isPrivate, mode, fundingType, entryFeeCents, fundingGoalCents, fundingDeadline })); }
    catch (e) { return handleError(e); }
  },

  // Every gathering the current host runs (standalone + club-hosted).
  async listMine() {
    try { return handleResponse(await api.get('/gatherings/mine')); }
    catch (e) { return handleError(e); }
  },

  async get(gatheringId) {
    try { return handleResponse(await api.get(`/gatherings/${gatheringId}`)); }
    catch (e) { return handleError(e); }
  },

  async update(gatheringId, patch) {
    try { return handleResponse(await api.put(`/gatherings/${gatheringId}`, patch)); }
    catch (e) { return handleError(e); }
  },

  async listAttachableEvents(gatheringId) {
    try { return handleResponse(await api.get(`/gatherings/${gatheringId}/attachable-events`)); }
    catch (e) { return handleError(e); }
  },

  async attachEvent(gatheringId, eventId) {
    try { return handleResponse(await api.post(`/gatherings/${gatheringId}/events`, { eventId })); }
    catch (e) { return handleError(e); }
  },

  async detachEvent(gatheringId, eventId) {
    try { return handleResponse(await api.delete(`/gatherings/${gatheringId}/events/${eventId}`)); }
    catch (e) { return handleError(e); }
  },

  async setRsvp(eventId, status) {
    try { return handleResponse(await api.post(`/events/${eventId}/rsvp`, { status })); }
    catch (e) { return handleError(e); }
  },

  // Set the same RSVP (going/maybe/cant) for every event in the gathering.
  async rsvpAll(gatheringId, status = 'going') {
    try { return handleResponse(await api.post(`/gatherings/${gatheringId}/rsvp-all`, { status })); }
    catch (e) { return handleError(e); }
  },

  // Buy all ticketed events in one payment (charged per event).
  async checkoutAllQuote(gatheringId) {
    try { return handleResponse(await api.get(`/gatherings/${gatheringId}/checkout-all/quote`)); }
    catch (e) { return handleError(e); }
  },

  async checkoutAll(gatheringId, { stripeCustomerId, stripePaymentMethodId, skipBalance = false }) {
    try { return handleResponse(await api.post(`/gatherings/${gatheringId}/checkout-all`, { stripeCustomerId, stripePaymentMethodId, skipBalance })); }
    catch (e) { return handleError(e); }
  },

  // Host guest-list view: per-event guest lists + "paid all" roster.
  async getManage(gatheringId) {
    try { return handleResponse(await api.get(`/gatherings/${gatheringId}/manage`)); }
    catch (e) { return handleError(e); }
  },

  // Invite a person to the gathering (host only, published gatherings).
  async invite(gatheringId, userId) {
    try { return handleResponse(await api.post(`/gatherings/${gatheringId}/invite`, { userId })); }
    catch (e) { return handleError(e); }
  },

  // Gatherings the signed-in person was invited to (the INVITED block on /guest).
  async myInvites() {
    try { return handleResponse(await api.get('/gatherings/invites/mine')); }
    catch (e) { return handleError(e); }
  },

  // Co-hosts run the whole gathering; only the owner adds or removes them (a co-host may step down).
  async addCoHost(gatheringId, userId) {
    try { return handleResponse(await api.post(`/gatherings/${gatheringId}/cohosts`, { userId })); }
    catch (e) { return handleError(e); }
  },
  async removeCoHost(gatheringId, userId) {
    try { return handleResponse(await api.delete(`/gatherings/${gatheringId}/cohosts/${userId}`)); }
    catch (e) { return handleError(e); }
  },

  // ── Gathering-level entry (apply / pay / curate / comp / greenlight) ──
  async applyQuote(gatheringId) {
    try { return handleResponse(await api.get(`/gatherings/${gatheringId}/apply/quote`)); }
    catch (e) { return handleError(e); }
  },
  async apply(gatheringId) {
    try { return handleResponse(await api.post(`/gatherings/${gatheringId}/apply`)); }
    catch (e) { return handleError(e); }
  },
  async applyConfirm(gatheringId, { stripeCustomerId, stripePaymentMethodId, skipBalance = false }) {
    try { return handleResponse(await api.post(`/gatherings/${gatheringId}/apply/confirm`, { stripeCustomerId, stripePaymentMethodId, skipBalance })); }
    catch (e) { return handleError(e); }
  },
  async listApplicants(gatheringId) {
    try { return handleResponse(await api.get(`/gatherings/${gatheringId}/applicants`)); }
    catch (e) { return handleError(e); }
  },
  async reviewApplicant(gatheringId, userId, action) {
    try { return handleResponse(await api.post(`/gatherings/${gatheringId}/applicants/${userId}/review`, { action })); }
    catch (e) { return handleError(e); }
  },
  async comp(gatheringId, userId) {
    try { return handleResponse(await api.post(`/gatherings/${gatheringId}/comp`, { userId })); }
    catch (e) { return handleError(e); }
  },
  async greenlight(gatheringId) {
    try { return handleResponse(await api.post(`/gatherings/${gatheringId}/greenlight`)); }
    catch (e) { return handleError(e); }
  },
  async withdrawApply(gatheringId) {
    try { return handleResponse(await api.post(`/gatherings/${gatheringId}/apply/withdraw`)); }
    catch (e) { return handleError(e); }
  },
  /** Host calls the gathering off: entry closes, paid entries are refunded; child events are untouched. */
  async cancel(gatheringId) {
    try { return handleResponse(await api.post(`/gatherings/${gatheringId}/cancel`)); }
    catch (e) { return handleError(e); }
  },

  // ── The gathering manager (host only): money, roster, refunds, door, native tickets ──
  async getMoney(gatheringId) {
    try { return handleResponse(await api.get(`/gatherings/${gatheringId}/money`)); }
    catch (e) { return handleError(e); }
  },
  async getRoster(gatheringId) {
    try { return handleResponse(await api.get(`/gatherings/${gatheringId}/roster`)); }
    catch (e) { return handleError(e); }
  },
  async refundAdmission(gatheringId, admissionId, reason) {
    try { return handleResponse(await api.post(`/gatherings/${gatheringId}/admissions/${admissionId}/refund`, { reason })); }
    catch (e) { return handleError(e); }
  },
  async doorScan(gatheringId, code) {
    try { return handleResponse(await api.post(`/gatherings/${gatheringId}/door/scan`, { code })); }
    catch (e) { return handleError(e); }
  },
  async doorCheckIn(gatheringId, admissionId) {
    try { return handleResponse(await api.post(`/gatherings/${gatheringId}/door/admissions/${admissionId}/check-in`)); }
    catch (e) { return handleError(e); }
  },
  async myTickets() {
    try { return handleResponse(await api.get('/gatherings/tickets/mine')); }
    catch (e) { return handleError(e); }
  },
  async getTicket(ticketId) {
    try { return handleResponse(await api.get(`/gatherings/tickets/${ticketId}`)); }
    catch (e) { return handleError(e); }
  },
  /** The seat behind a QR (`/gt/{code}`) — holder, buyer or host. */
  async getTicketByCode(code) {
    try { return handleResponse(await api.get(`/gatherings/tickets/by-code/${code}`)); }
    catch (e) { return handleError(e); }
  },
  // ── Passing a seat on ──
  async createTransfer(ticketId, { email = null, recipientName = null } = {}) {
    try { return handleResponse(await api.post(`/gatherings/tickets/${ticketId}/transfer`, { email, recipientName })); }
    catch (e) { return handleError(e); }
  },
  async cancelTransfer(transferId) {
    try { return handleResponse(await api.delete(`/gatherings/tickets/transfers/${transferId}`)); }
    catch (e) { return handleError(e); }
  },
  async previewClaim(token) {
    try { return handleResponse(await api.get(`/gatherings/tickets/claim/${token}`)); }
    catch (e) { return handleError(e); }
  },
  async claim(token) {
    try { return handleResponse(await api.post(`/gatherings/tickets/claim/${token}`)); }
    catch (e) { return handleError(e); }
  },

  // ── Announcements (gathering's own feed) ──
  async getAnnouncements(gatheringId) {
    try { return handleResponse(await api.get(`/gatherings/${gatheringId}/announcements`)); }
    catch (e) { return handleError(e); }
  },
  async createAnnouncement(gatheringId, { content, isPinned = false }) {
    try { return handleResponse(await api.post(`/gatherings/${gatheringId}/announcements`, { content, isPinned })); }
    catch (e) { return handleError(e); }
  },
  async deleteAnnouncement(gatheringId, announcementId) {
    try { return handleResponse(await api.delete(`/gatherings/${gatheringId}/announcements/${announcementId}`)); }
    catch (e) { return handleError(e); }
  },

  // ── Tasks ──
  async getTasks(gatheringId) {
    try { return handleResponse(await api.get(`/gatherings/${gatheringId}/tasks`)); }
    catch (e) { return handleError(e); }
  },
  async createTask(gatheringId, { title, parentTaskId = null }) {
    try { return handleResponse(await api.post(`/gatherings/${gatheringId}/tasks`, { title, parentTaskId })); }
    catch (e) { return handleError(e); }
  },
  async claimTask(gatheringId, taskId, claim = true) {
    try { return handleResponse(await api.post(`/gatherings/${gatheringId}/tasks/${taskId}/claim`, { claim })); }
    catch (e) { return handleError(e); }
  },
  async assignTask(gatheringId, taskId, userId) {
    try { return handleResponse(await api.post(`/gatherings/${gatheringId}/tasks/${taskId}/assign`, { userId })); }
    catch (e) { return handleError(e); }
  },
  async deleteTask(gatheringId, taskId) {
    try { return handleResponse(await api.delete(`/gatherings/${gatheringId}/tasks/${taskId}`)); }
    catch (e) { return handleError(e); }
  },

  async delete(gatheringId) {
    try { return handleResponse(await api.delete(`/gatherings/${gatheringId}`)); }
    catch (e) { return handleError(e); }
  },

  // ── Cover (Oct 2026): the club / Pro cover document on a gathering ──
  async uploadCoverPhoto(gatheringId, file) {
    try {
      const form = new FormData();
      form.append('file', file);
      return handleResponse(await api.post(`/gatherings/${gatheringId}/photos/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' } }));
    } catch (e) { return handleError(e); }
  },
  /** EDIT COVER: layout · split · photoCount · order · focal · cardId · addPaths · remove. */
  async updateCover(gatheringId, data) {
    try { return handleResponse(await api.put(`/gatherings/${gatheringId}/cover`, data)); }
    catch (e) { return handleError(e); }
  },
};

export default gatheringService;
